import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { useEffect, useState } from 'react'
import type { BiluEntryType } from '@/helpers/api/user'

export interface WechatBiluLaunchInfo {
  username: string
  path: string
}

interface BiluEntryPayload {
  url: string
  wxMnpId?: string
}

export async function getBiluEntryPayload(
  scene: 'wechat_h5' | 'mobile_browser',
  entry: BiluEntryType
): Promise<BiluEntryPayload | undefined> {
  const { data } = await api.user.getBiluSsoEntry(scene, entry)
  const responseData = typeof data === 'string' ? { url: data } : data
  const targetUrl = responseData?.url

  if (!targetUrl) {
    return undefined
  }

  return {
    url: targetUrl,
    wxMnpId: responseData?.wxMnpId,
  }
}

export function useWechatBiluLaunchInfo(
  enabled: boolean,
  isLoggedIn: boolean,
  entry: BiluEntryType
): WechatBiluLaunchInfo | undefined {
  const [launchInfo, setLaunchInfo] = useState<WechatBiluLaunchInfo | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !isLoggedIn || !commonUtils.isWechat()) {
      setLaunchInfo(undefined)
      return
    }

    let disposed = false

    const initWechatLaunchInfo = async (): Promise<void> => {
      try {
        const payload = await getBiluEntryPayload('wechat_h5', entry)

        if (!payload?.url || !payload.wxMnpId) {
          if (!disposed) {
            setLaunchInfo(undefined)
          }
          return
        }

        if (!disposed) {
          setLaunchInfo({
            username: payload.wxMnpId,
            path: payload.url,
          })
        }

        const wxModule = await import('weixin-js-sdk')
        const wx = (wxModule.default || wxModule) as any
        const currentUrl = window.location.href.split('#')[0]
        const { data: config } = await api.user.getWechatJsSdkConfig({
          url: currentUrl,
        })

        wx.config({
          debug: false,
          appId: config.appId,
          timestamp: config.timestamp,
          nonceStr: config.nonceStr,
          signature: config.signature,
          jsApiList: [],
          openTagList: ['wx-open-launch-weapp'],
        })
      } catch (error) {
        console.error('init wechat bilu launch failed', error)
      }
    }

    initWechatLaunchInfo()

    return () => {
      disposed = true
    }
  }, [enabled, entry, isLoggedIn])

  return launchInfo
}
